'use client';

import * as React from 'react';
import { c } from '../../../utils/tailwind-merge';
import { TableBodyProps } from './Table.types';

function Table({
  className = '',
  ...props
}: React.ComponentPropsWithoutRef<'table'>) {
  return (
    <div
      data-slot="table-container"
      className={c('relative w-full overflow-x-auto tracking-[-0.028px]')}
    >
      <table
        data-slot="table"
        className={c(
          'w-full table-auto border-separate border-spacing-0 text-sm',
          className
        )}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={className} {...props} />;
}

function TableBody({
  variant,
  className = '',
  rightColTextAlign = 'right',
  ...props
}: TableBodyProps) {
  const alternateTableColors =
    '[&>tr:nth-child(even)>:is(td,th)]:bg-white ' +
    '[&>tr:nth-child(odd)>:is(td,th)]:bg-grey-100 ' +
    '[&>tr:nth-child(odd)>:is(td,th):first-child]:rounded-l-lg ' +
    '[&>tr:nth-child(odd)>:is(td,th):last-child]:rounded-r-lg' +
    '[&>tr>:is(td,th):first-child]:overflow-hidden' +
    '[&>tr>:is(td,th):last-child]:overflow-hidden';

  const alternateWhiteTableColors =
    '[&>tr:nth-child(odd)>:is(td,th)]:bg-white ' +
    '[&>tr:nth-child(even)>:is(td,th)]:bg-grey-100 ' +
    '[&>tr:nth-child(even)>:is(td,th):first-child]:rounded-l-lg ' +
    '[&>tr:nth-child(even)>:is(td,th):last-child]:rounded-r-lg';

  const alternateTablePx = `
    [&>tr>:is(td,th):first-child]:pl-[11px]
    [&>tr>:is(td,th):last-child]:pr-[10px]
    [&>tr>:is(td,th)]:pb-[13px]
    [&>tr>:is(td,th)]:pt-[9px]
  `;

  const isAlternate = variant === 'alternate' || variant === 'alternate-white';

  return (
    <tbody
      data-slot="table-body"
      className={c(
        isAlternate
          ? `${variant === 'alternate-white' ? alternateWhiteTableColors : alternateTableColors} ${alternateTablePx}`
          : '[&>tr>:is(td,th)]:px-0 [&>tr>:is(td,th)]:py-1',
        'break-all [&>tr>:is(td,th):first-child]:gap-x-1 [&>tr>:is(td,th):first-child]:text-nowrap [&>tr>:is(td,th):first-child]:align-middle [&>tr>:is(td,th):first-child]:text-grey-700',
        rightColTextAlign === 'right'
          ? '[&>tr>:is(td,th):last-child]:text-right'
          : '[&>tr>:is(td,th):last-child]:text-left',
        '[&>tr>:is(td,th):last-child]:text-grey-900',
        '[&>tr>:is(th)]:text-left [&>tr>:is(th)]:font-normal',
        className
      )}
      {...props}
    />
  );
}

function TableRow({ className = '', ...props }: React.ComponentProps<'tr'>) {
  return <tr data-slot="table-row" className={className} {...props} />;
}

const commonCellStyles = c(
  '[&_span]:tiny-text-link [&_span]:flex [&_span]:justify-end [&_span]:text-blue-hover', // Support for LinkButton styles
  '[&_svg]:ml-1',
  'break-all'
);

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={c(commonCellStyles, className)}
      {...props}
    />
  );
}

function TableCell({ className = '', ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={c(commonCellStyles, className)}
      {...props}
    />
  );
}

function TableCaption({
  className = '',
  ...props
}: React.ComponentPropsWithoutRef<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={c(
        'tiny-text text-muted-foreground mb-4 caption-top break-all text-left font-medium leading-5',
        className
      )}
      {...props}
    />
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
};
